// ────────────────────────────────────────────────────────────────────────────
// The template-driven document renderer.
//
// One component turns any `(template, data)` pair into a react-pdf document.
// This replaces the hardcoded `InvoicePdfDocument` — the "Stratum Standard"
// stock template reproduces it, so the finance tabs can switch over without
// their output changing.
//
// Styles are built inline from `layout.theme` rather than via
// `StyleSheet.create`, because every value here is user-controlled and the
// stylesheet cache would need invalidating on each keystroke in the builder.
//
// react-pdf constraints worth knowing before editing:
//   * `<Text>` may not contain a `<View>` — rows are Views of Texts.
//   * only the standard PDF font families work without `Font.register`, and
//     bold is a *separate family name* (Helvetica-Bold, not fontWeight).
//   * the footer is `fixed` + absolutely positioned, so the page reserves
//     bottom padding for it instead of letting content run underneath.
//   * table header rows are not repeated on page 2+ (react-pdf has no
//     repeat-header primitive for inline flow) — same as today's PDF.
// ────────────────────────────────────────────────────────────────────────────
import { Document, Image, Page, Path, Svg, Text, View } from '@react-pdf/renderer';
import type {
  DocumentData,
  DocumentLine,
} from '../lib/documentData';
import { WORDED_TAX_TYPES } from '../lib/documentData';
import { formatAddressBlock } from '../lib/addressFormat';
import { dedupeLayout } from '../lib/migrateLayout';
import { resolveLabel } from '../lib/labels';
import { lineCellText, lineRouteEnds, markContinuations } from '../lib/lineColumns';
import { organisationFieldLines } from '../lib/organisationFields';
import { MIN_FLEX_COLUMN_PT, resolveFlexKey } from '../lib/pageMetrics';
import { buildTokenMap, formatDate, formatMoney, resolveTokens } from '../lib/tokens';
import { resolveTypeScale, type ResolvedTypeScale } from '../lib/typeScale';
import {
  BANK_FIELD_LABEL,
  META_FIELD_LABEL,
  TOTAL_ROW_LABEL,
  type Align,
  type BankBand,
  type CustomerBand,
  type DocDetailsBand,
  type DocumentLayout,
  type DocumentTemplate,
  type FontFamily,
  type Band,
  type FooterBand,
  type LineColumn,
  type LinesBand,
  type LogoBand,
  type MetaField,
  type OrganisationBand,
  type TextBand,
  type ThemeSettings,
  type TitleBand,
  type TotalsBand,
} from '../lib/types';

/** Horizontal gap between the columns of a column row. */
const COLUMN_GAP_PT = 12;

/** Relative width of column `index`; equal when no weights are configured. */
function columnWeight(weights: number[] | undefined, index: number): number {
  const w = weights?.[index];
  return w && w > 0 ? w : 1;
}

/**
 * Split the body into the header grid and everything after it.
 *
 * The boundary is the line table: everything above it is the masthead and is
 * laid out as ONE grid, everything from it down stacks full width. Anchoring on
 * the line table rather than a marker band means the region is whatever the
 * operator put above their charges, with nothing to keep in sync.
 */
function splitAtLines(bands: Band[]): { header: Band[]; rest: Band[] } {
  const i = bands.findIndex((b) => b.kind === 'lines');
  // No line table: treat everything as body rather than turning the whole
  // document into a masthead.
  if (i === -1) return { header: [], rest: bands };
  return { header: bands.slice(0, i), rest: bands.slice(i) };
}

/**
 * Columns the header grid uses: the operator's choice, else as many as its bands
 * actually occupy. The fallback is what keeps a layout that predates the setting
 * looking the way it already did.
 */
function headerColumnCount(layout: DocumentLayout, header: Band[]): number {
  if (layout.headerColumns) return layout.headerColumns;
  let used = 1;
  for (const band of header) used = Math.max(used, band.column ?? 1);
  return used;
}

/** Gap under the grid: the explicit setting, else the largest its bands ask for. */
function headerGapAfter(layout: DocumentLayout, header: Band[]): number {
  if (layout.headerGapAfterPt !== undefined) return layout.headerGapAfterPt;
  let gap = 0;
  for (const band of header) gap = Math.max(gap, band.spaceAfterPt);
  return gap;
}

/** Standard-PDF bold companion for each supported family. */
const BOLD_FAMILY: Record<FontFamily, string> = {
  Helvetica: 'Helvetica-Bold',
  'Times-Roman': 'Times-Bold',
  Courier: 'Courier-Bold',
};

/** Height reserved at the bottom of every page for the fixed footer. */
const FOOTER_RESERVE_PT = 34;

function alignItems(align: Align): 'flex-start' | 'center' | 'flex-end' {
  return align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
}

/** Blank-ish values are dropped rather than printed as an empty line. */
function present(value: string | null | undefined): value is string {
  return Boolean(value && value.trim() && value.trim() !== '—');
}

interface Ctx {
  theme: ThemeSettings;
  /** Resolved sizes — never read `baseFontSizePt` directly for anything but body. */
  scale: ResolvedTypeScale;
  bold: string;
  tokens: Record<string, string>;
  data: DocumentData;
}

// ── Small shared pieces ────────────────────────────────────────────────────

function Label({ ctx, children }: { ctx: Ctx; children: string }) {
  return (
    <Text
      style={{
        color: ctx.theme.mutedColor,
        fontSize: ctx.scale.small,
        textTransform: 'uppercase',
        marginBottom: 3,
      }}
    >
      {children}
    </Text>
  );
}

/** `label   value` pair used by the meta and bank blocks. */
function KeyValue({
  ctx,
  label,
  value,
  labelWidth = 70,
  align = 'left',
}: {
  ctx: Ctx;
  label: string;
  value: string;
  labelWidth?: number;
  align?: Align;
}) {
  return (
    <View style={{ flexDirection: 'row', marginBottom: 2 }}>
      <Text
        style={{
          color: ctx.theme.mutedColor,
          width: labelWidth,
          textAlign: align === 'right' ? 'right' : 'left',
          marginRight: 8,
        }}
      >
        {label}
      </Text>
      <Text>{value}</Text>
    </View>
  );
}

// ── Bands ──────────────────────────────────────────────────────────────────

function LogoBandView({ ctx, band }: { ctx: Ctx; band: LogoBand }) {
  const logoUrl = ctx.data.organisation.logoUrl;
  // react-pdf fetches `<Image src>` itself and a rejected fetch fails the whole
  // document, so only http(s)/data sources are attempted.
  if (!logoUrl || !/^(https?:|data:)/i.test(logoUrl)) return null;
  return (
    <View style={{ alignItems: alignItems(band.align) }}>
      <Image src={logoUrl} style={{ width: band.widthPt }} />
    </View>
  );
}

function TitleBandView({ ctx, band }: { ctx: Ctx; band: TitleBand }) {
  const titleText = resolveTokens(band.text, ctx.tokens);
  return (
    <View>
      <View style={{ alignItems: alignItems(band.align) }}>
        <Text
          style={{
            fontFamily: ctx.bold,
            fontSize: band.sizePt,
            color: ctx.theme.accentColor,
          }}
        >
          {band.uppercase ? titleText.toUpperCase() : titleText}
        </Text>
        {band.showNumber ? (
          <Text style={{ marginTop: 2, color: ctx.theme.accentColor }}>
            {present(band.numberLabel)
              ? `${band.numberLabel} ${ctx.data.doc.number}`
              : ctx.data.doc.number}
          </Text>
        ) : null}
      </View>
      {band.showRule ? (
        <View style={{ marginTop: 8, borderTopWidth: 1, borderColor: ctx.theme.borderColor }} />
      ) : null}
    </View>
  );
}

function OrganisationBandView({ ctx, band }: { ctx: Ctx; band: OrganisationBand }) {
  const align = band.align;
  const textAlign = align === 'center' ? 'center' : align === 'right' ? 'right' : 'left';

  // Each field's printed text comes from `organisationFieldLines`, which
  // applies the band's per-field override on top of the organisation record.
  // The builder shows the un-overridden result as the edit box's placeholder,
  // so both sides must go through this one function.
  return (
    <View style={{ alignItems: alignItems(align) }}>
      {band.fields.map((field) => {
        const lines = organisationFieldLines(field, ctx.data.organisation, band.overrides);
        if (!lines.length) return null;

        // The name is the letterhead line — larger and bold. Everything else is
        // secondary detail. Alignment is applied as a second style object so the
        // union of these two shapes cannot widen `textAlign` to `string`.
        const fieldStyle =
          field === 'name'
            ? { fontFamily: ctx.bold, fontSize: ctx.scale.organisationName }
            : { color: ctx.theme.mutedColor, marginTop: 2 };

        return lines.map((line, i) => (
          <Text key={`${field}-${i}`} style={[fieldStyle, { textAlign }]}>
            {line}
          </Text>
        ));
      })}
    </View>
  );
}


function CustomerBandView({ ctx, band }: { ctx: Ctx; band: CustomerBand }) {
  const c = ctx.data.customer;
  // A reverse-charge invoice must show the customer's VAT number — printed
  // after the chosen fields when the layout does not list it.
  const forceVatNumber =
    !!ctx.data.customerVatNumberRequired && !band.fields.includes('taxNumber') && present(c.taxNumber);
  return (
    <View>
      {present(band.heading) ? <Label ctx={ctx}>{band.heading}</Label> : null}

      {band.fields.map((field) => {
        switch (field) {
          case 'name':
            return (
              <Text key={field} style={{ fontFamily: ctx.bold }}>
                {c.name}
              </Text>
            );
          case 'code':
            return present(c.code) ? (
              <Text key={field} style={{ color: ctx.theme.mutedColor, marginTop: 2 }}>
                {c.code}
              </Text>
            ) : null;
          case 'addressLines':
            // The stored address is one comma-separated string; the band decides
            // how it breaks. Defaults to a single wrapping line.
            return formatAddressBlock(c.address, band.addressFormat, band.addressWrapChars).map(
              (line, i) => (
                <Text key={`caddr-${i}`} style={{ color: ctx.theme.mutedColor, marginTop: 2 }}>
                  {line}
                </Text>
              ),
            );
          case 'taxNumber':
            return present(c.taxNumber) ? (
              <Text key={field} style={{ color: ctx.theme.mutedColor, marginTop: 2 }}>
                {`VAT ${c.taxNumber}`}
              </Text>
            ) : null;
          case 'contactName':
            return present(c.contactName) ? (
              <Text key={field} style={{ color: ctx.theme.mutedColor, marginTop: 2 }}>
                {c.contactName}
              </Text>
            ) : null;
          case 'phone':
            return present(c.phone) ? (
              <Text key={field} style={{ color: ctx.theme.mutedColor, marginTop: 2 }}>
                {`Tel ${c.phone}`}
              </Text>
            ) : null;
          case 'email':
            return present(c.email) ? (
              <Text key={field} style={{ color: ctx.theme.mutedColor, marginTop: 2 }}>
                {c.email}
              </Text>
            ) : null;
        }
      })}
      {forceVatNumber ? (
        <Text style={{ color: ctx.theme.mutedColor, marginTop: 2 }}>{`VAT ${c.taxNumber}`}</Text>
      ) : null}
    </View>
  );
}

function docDetailValue(ctx: Ctx, key: MetaField): string | null {
  const d = ctx.data.doc;
  switch (key) {
    case 'issueDate':
      return formatDate(d.issueDate);
    case 'dueDate':
      return formatDate(d.dueDate);
    case 'poNumber':
      return present(d.poNumber) ? d.poNumber : null;
    case 'reference':
      return present(d.reference) ? d.reference : null;
    case 'currency':
      return d.currencyIsoCode;
    case 'paymentTerms':
      return present(d.paymentTerms) ? d.paymentTerms : null;
  }
}

function DocDetailsBandView({ ctx, band }: { ctx: Ctx; band: DocDetailsBand }) {
  const rows = band.fields
    .map((key) => ({
      key,
      // A renamed label wins — "PO number" is often "Your order ref".
      label: resolveLabel(band.labels, key, META_FIELD_LABEL),
      value: docDetailValue(ctx, key),
    }))
    .filter((row): row is { key: typeof row.key; label: string; value: string } => row.value !== null);

  if (!rows.length) return null;

  // Labels are user-editable, so a fixed column would clip "Your customer order
  // reference". Size it from the longest label actually in use — roughly half
  // the body size per character for the standard families — with a floor so the
  // default labels keep their familiar spacing, and a ceiling so one long label
  // cannot push the values off the page.
  let longest = 0;
  for (const row of rows) longest = Math.max(longest, row.label.length);
  const labelWidth = Math.min(150, Math.max(64, Math.ceil(longest * ctx.theme.baseFontSizePt * 0.52)));

  return (
    <View style={{ alignItems: band.align === 'right' ? 'flex-end' : 'flex-start' }}>
      {present(band.heading) ? <Label ctx={ctx}>{band.heading}</Label> : null}
      {rows.map((row) => (
        <KeyValue
          key={row.key}
          ctx={ctx}
          label={row.label}
          value={row.value}
          align={band.align}
          labelWidth={labelWidth}
        />
      ))}
    </View>
  );
}

/**
 * A left-to-right arrow, drawn rather than typed.
 *
 * There is no arrow glyph to type: react-pdf's standard fonts are WinAnsi and
 * that set has none, so U+2192 comes out as a right quote (and only Courier /
 * Helvetica / Times are available without registering a font file, which would
 * mean shipping one and honouring its embedding licence). Vector primitives
 * cost nothing and scale with the type size.
 *
 * Sized off the surrounding font size so it stays proportional when the line
 * type scale changes, and stroked in the text colour so it reads as punctuation
 * rather than as a graphic.
 */
function RouteArrow({ sizePt, color }: { sizePt: number; color: string }) {
  // Kept to about the footprint of the en dash it replaces — an arrow as wide
  // as a character or two reads as punctuation; any longer and the shaft starts
  // competing with the place names on either side of it.
  const width = sizePt * 0.8;
  const mid = sizePt / 2;
  const head = sizePt * 0.17;
  const stroke = Math.max(0.4, sizePt * 0.055);
  return (
    <Svg
      width={width}
      height={sizePt}
      viewBox={`0 0 ${width} ${sizePt}`}
      style={{ marginHorizontal: sizePt * 0.2 }}
    >
      <Path d={`M0 ${mid} H${width - stroke}`} stroke={color} strokeWidth={stroke} strokeLinecap="round" />
      <Path
        d={`M${width - head - stroke} ${mid - head} L${width - stroke} ${mid} L${width - head - stroke} ${mid + head}`}
        stroke={color}
        strokeWidth={stroke}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

function LinesBandView({ ctx, band }: { ctx: Ctx; band: LinesBand }) {
  const productsDisplay = band.productsDisplay ?? 'hidden';
  // The products column prints only in 'column' mode, whatever its tick says.
  const visible = band.columns.filter(
    (c) => c.visible && (c.key !== 'products' || productsDisplay === 'column'),
  );
  const first = visible[0];
  if (!first) return null;

  // Exactly one column stretches, resolved by the SAME rule the inspector uses
  // (`resolveFlexKey`) — a second opinion here is how a zero-width column ends
  // up collapsed while another one stretches.
  const flexKey = resolveFlexKey(band.columns) ?? first.key;

  // `minWidth` keeps the flexible column readable when the fixed columns claim
  // more than the page has: the table runs wide (and the last columns clip)
  // rather than wrapping descriptions one character per line. The inspector
  // warns before it gets that far — see `measureColumns`.
  const cellStyle = (col: LineColumn) => ({
    ...(col.key === flexKey
      ? { flex: 1, minWidth: MIN_FLEX_COLUMN_PT }
      : { width: col.widthPt }),
    textAlign: col.align,
    paddingRight: 4,
  });

  // Continuation rows are the ones whose context cells blank out, so a fuel
  // surcharge prints under its order without repeating the date and order number.
  const blankRepeats = band.blankRepeatedContext ?? true;
  const marked = markContinuations(ctx.data.lines);

  const rows: Array<
    { kind: 'group'; label: string } | { kind: 'line'; line: DocumentLine; isContinuation: boolean }
  > = [];
  if (band.groupByOrder) {
    // Preserve first-seen order rather than sorting — invoice line sequence is
    // meaningful and re-sorting would reshuffle what the operator arranged.
    const seen = new Set<string>();
    for (const entry of marked) {
      const group = present(entry.line.orderNumber) ? entry.line.orderNumber : 'Other charges';
      if (!seen.has(group)) {
        seen.add(group);
        rows.push({ kind: 'group', label: group });
        for (const inner of marked) {
          const g = present(inner.line.orderNumber) ? inner.line.orderNumber : 'Other charges';
          if (g === group) {
            rows.push({ kind: 'line', line: inner.line, isContinuation: inner.isContinuation });
          }
        }
      }
    }
  } else {
    for (const entry of marked) {
      rows.push({ kind: 'line', line: entry.line, isContinuation: entry.isContinuation });
    }
  }

  let zebraIndex = 0;
  return (
    // fontSize on the wrapper cascades to the header and every row, so the table
    // can run denser than the rest of the document without touching each cell.
    <View
      style={{
        borderTopWidth: 1,
        borderColor: ctx.theme.borderColor,
        fontSize: ctx.scale.line,
      }}
    >
      {band.showHeader ? (
        <View
          style={{
            flexDirection: 'row',
            backgroundColor: ctx.theme.tableHeadColor,
            borderBottomWidth: 1,
            borderColor: ctx.theme.borderColor,
            paddingVertical: 5,
            paddingHorizontal: 2,
          }}
        >
          {visible.map((col) => (
            <Text
              key={col.key}
              style={[cellStyle(col), { fontFamily: ctx.bold, color: ctx.theme.accentColor }]}
            >
              {col.label}
            </Text>
          ))}
        </View>
      ) : null}

      {rows.map((row, i) => {
        if (row.kind === 'group') {
          return (
            <View
              key={`g-${row.label}-${i}`}
              style={{
                borderBottomWidth: 1,
                borderColor: ctx.theme.borderColor,
                paddingVertical: 4,
                paddingHorizontal: 2,
              }}
              wrap={false}
            >
              <Text style={{ fontFamily: ctx.bold, color: ctx.theme.accentColor }}>{row.label}</Text>
            </View>
          );
        }
        const striped = band.zebra && zebraIndex++ % 2 === 1;
        return (
          <View
            key={row.line.id}
            style={{
              flexDirection: 'row',
              borderBottomWidth: 1,
              borderColor: ctx.theme.borderColor,
              paddingVertical: 5,
              paddingHorizontal: 2,
              ...(striped ? { backgroundColor: ctx.theme.tableHeadColor } : null),
            }}
            wrap={false}
          >
            {visible.map((col) => {
              const cellOpts = {
                currencyIsoCode: ctx.data.doc.currencyIsoCode,
                isContinuation: blankRepeats && row.isContinuation,
              };
              const route = lineRouteEnds(col, row.line, cellOpts);
              if (route) {
                // Three flex children rather than one text flow, because the
                // arrow is a drawn element and react-pdf cannot put a View
                // inside a Text. `flexWrap` is what keeps a narrow column
                // working: each end wraps within the cell instead of
                // overflowing it.
                return (
                  <View
                    key={col.key}
                    style={[
                      cellStyle(col),
                      { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' },
                    ]}
                  >
                    <Text>{route.from}</Text>
                    <RouteArrow sizePt={ctx.scale.line} color={ctx.theme.textColor} />
                    <Text>{route.to}</Text>
                  </View>
                );
              }
              // Products under the description: a second, smaller line in the
              // description cell. Not on a continuation — the transport line
              // above already said what was carried.
              const productsBelow =
                col.key === 'description' &&
                productsDisplay === 'belowDescription' &&
                !cellOpts.isContinuation &&
                present(row.line.products);
              if (productsBelow) {
                return (
                  <View key={col.key} style={cellStyle(col)}>
                    <Text>{lineCellText(col, row.line, cellOpts)}</Text>
                    <Text style={{ color: ctx.theme.mutedColor, fontSize: ctx.scale.line * 0.85, marginTop: 1 }}>
                      {row.line.products}
                    </Text>
                  </View>
                );
              }
              return (
                <Text key={col.key} style={cellStyle(col)}>
                  {lineCellText(col, row.line, cellOpts)}
                </Text>
              );
            })}
          </View>
        );
      })}
    </View>
  );
}

function TotalsBandView({ ctx, band }: { ctx: Ctx; band: TotalsBand }) {
  const iso = ctx.data.doc.currencyIsoCode;
  const t = ctx.data.totals;
  const amount = (key: (typeof band.rows)[number]): number =>
    key === 'subtotal' ? t.subtotal : key === 'vatTotal' ? t.vatTotal : t.total;

  // The breakdown lines borrow the VAT row's label, so renaming "VAT" to "Sales
  // Tax" renames "VAT 23% on …" with it. Two settings for one concept would
  // guarantee they drift apart on somebody's invoice.
  const vatLabel = resolveLabel(band.rowLabels, 'vatTotal', TOTAL_ROW_LABEL);

  return (
    <View
      style={{
        width: band.widthPt,
        marginLeft: band.align === 'right' ? 'auto' : 0,
      }}
    >
      {/* One row per tax schema. A worded treatment (reverse charge, outside
          scope, zero-rated, exempt) prints even with the breakdown off: its
          wording is a statement the invoice has to make, not a nicety. */}
      {t.vatBands
        .filter((vb) => band.showVatBreakdown || (vb.label && WORDED_TAX_TYPES.has(vb.taxType ?? '')))
        .map((vb) => (
          <View
            key={vb.key}
            style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 2 }}
          >
            <Text style={{ color: ctx.theme.mutedColor, flex: 1, paddingRight: 6 }}>
              {vb.label
                ? `${vb.label} (${vb.ratePct}%) on ${formatMoney(vb.net, iso)}`
                : `${vatLabel} ${vb.ratePct}% on ${formatMoney(vb.net, iso)}`}
            </Text>
            <Text style={{ color: ctx.theme.mutedColor }}>{formatMoney(vb.vat, iso)}</Text>
          </View>
        ))}

      {band.rows.map((key) => {
        const isGrand = key === 'total';
        return (
          <View
            key={key}
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              paddingVertical: 2,
              ...(isGrand
                ? {
                    borderTopWidth: 1,
                    borderColor: ctx.theme.borderColor,
                    marginTop: 3,
                    paddingTop: 4,
                    fontFamily: ctx.bold,
                    fontSize: ctx.scale.total,
                  }
                : null),
            }}
          >
            <Text>{resolveLabel(band.rowLabels, key, TOTAL_ROW_LABEL)}</Text>
            <Text>{formatMoney(amount(key), iso)}</Text>
          </View>
        );
      })}
    </View>
  );
}

function TextBandView({ ctx, band }: { ctx: Ctx; band: TextBand }) {
  const body = resolveTokens(band.body, ctx.tokens);
  // A heading with nothing under it is noise — an empty body (or one whose
  // only token resolved to "no value") drops the band entirely.
  if (!present(body)) return null;

  const textAlign = band.align === 'center' ? 'center' : band.align === 'right' ? 'right' : 'left';
  return (
    <View
      style={
        band.boxed
          ? {
              borderWidth: 1,
              borderColor: ctx.theme.borderColor,
              borderRadius: 3,
              padding: 8,
            }
          : undefined
      }
    >
      {present(band.heading) ? <Label ctx={ctx}>{band.heading}</Label> : null}
      <Text style={{ textAlign }}>{body}</Text>
    </View>
  );
}

function BankBandView({ ctx, band }: { ctx: Ctx; band: BankBand }) {
  const bank = ctx.data.bank;
  const rows = band.fields
    .map((key) => ({ key, label: BANK_FIELD_LABEL[key], value: bank?.[key] }))
    .filter((row): row is { key: typeof row.key; label: string; value: string } =>
      present(row.value),
    );
  if (!rows.length) return null;

  return (
    <View
      style={
        band.boxed
          ? {
              borderWidth: 1,
              borderColor: ctx.theme.borderColor,
              borderRadius: 3,
              padding: 8,
            }
          : undefined
      }
    >
      {present(band.heading) ? <Label ctx={ctx}>{band.heading}</Label> : null}
      {rows.map((row) => (
        <KeyValue key={row.key} ctx={ctx} label={row.label} value={row.value} labelWidth={92} />
      ))}
    </View>
  );
}

function FooterBandView({
  ctx,
  band,
  marginPt,
}: {
  ctx: Ctx;
  band: FooterBand;
  marginPt: number;
}) {
  const body = resolveTokens(band.body, ctx.tokens);
  const textAlign = band.align === 'center' ? 'center' : band.align === 'right' ? 'right' : 'left';
  return (
    <View
      fixed
      style={{
        position: 'absolute',
        bottom: marginPt / 2,
        left: marginPt,
        right: marginPt,
        paddingTop: 6,
        ...(band.showRule ? { borderTopWidth: 1, borderColor: ctx.theme.borderColor } : null),
      }}
    >
      {present(body) ? (
        <Text
          style={{ textAlign, color: ctx.theme.mutedColor, fontSize: ctx.scale.small }}
        >
          {body}
        </Text>
      ) : null}
      {band.showPageNumbers ? (
        <Text
          style={{
            textAlign,
            color: ctx.theme.mutedColor,
            fontSize: ctx.scale.small,
            marginTop: 2,
          }}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
        />
      ) : null}
    </View>
  );
}

/** Renders whichever band this is. */
function BandContent({ ctx, band }: { ctx: Ctx; band: Band }) {
  switch (band.kind) {
    case 'logo':
      return <LogoBandView ctx={ctx} band={band} />;
    case 'title':
      return <TitleBandView ctx={ctx} band={band} />;
    case 'organisation':
      return <OrganisationBandView ctx={ctx} band={band} />;
    case 'customer':
      return <CustomerBandView ctx={ctx} band={band} />;
    case 'docDetails':
      return <DocDetailsBandView ctx={ctx} band={band} />;
    case 'lines':
      return (
        <>
          {/* A credit note names what it credits, and why — whatever the layout. */}
          {ctx.data.doc.creditNoteFor ? (
            <View style={{ marginBottom: 6 }}>
              <Text style={{ fontFamily: ctx.bold }}>{ctx.data.doc.creditNoteFor}</Text>
              {ctx.data.doc.creditReason ? (
                <Text style={{ color: ctx.theme.mutedColor, marginTop: 1 }}>
                  {`Reason: ${ctx.data.doc.creditReason}`}
                </Text>
              ) : null}
            </View>
          ) : null}
          <LinesBandView ctx={ctx} band={band} />
        </>
      );
    case 'totals':
      return <TotalsBandView ctx={ctx} band={band} />;
    case 'text':
      return <TextBandView ctx={ctx} band={band} />;
    case 'bank':
      return <BankBandView ctx={ctx} band={band} />;
    case 'footer':
      // Drawn once per page by the footer path, never inline.
      return null;
  }
}

// ── Document ───────────────────────────────────────────────────────────────

export interface DocumentPdfProps {
  template: DocumentTemplate;
  data: DocumentData;
}

/** Renders `data` through `template`. Any template, any of the two doc kinds. */
export function DocumentPdf({ template, data }: DocumentPdfProps) {
  // Deduplicated HERE, at the point of consumption, not only where layouts are
  // loaded and edited.
  //
  // A layout carrying the same band or column twice prints it twice while every
  // list in the builder — keyed by `band.id` / `col.key` — collapses it to a
  // single row, which reads as the renderer inventing content. Guarding the
  // loader and the editor is not enough on its own: a draft held in component
  // state, a cached query result, or a caller that builds a layout by hand all
  // reach this function without passing through either. The renderer is the one
  // place they all go through, and the pass is free when there is nothing to
  // drop (it returns the same object).
  const layout = dedupeLayout(template.layout);
  const { page, theme, bands, columnWeights } = layout;
  const ctx: Ctx = {
    theme,
    scale: resolveTypeScale(theme),
    bold: BOLD_FAMILY[theme.fontFamily],
    tokens: buildTokenMap(data),
    data,
  };

  const footer = bands.find((b): b is FooterBand => b.kind === 'footer' && b.enabled);
  const bodyBands = bands.filter((b) => b.enabled && b.kind !== 'footer');
  const { header: headerBands, rest: restBands } = splitAtLines(bodyBands);
  const headerColumns = headerColumnCount(layout, headerBands);

  return (
    <Document title={`${data.doc.typeName} ${data.doc.number}`}>
      <Page
        size={page.size}
        orientation={page.orientation ?? 'portrait'}
        style={{
          paddingTop: page.marginPt,
          paddingLeft: page.marginPt,
          paddingRight: page.marginPt,
          paddingBottom: footer ? page.marginPt + FOOTER_RESERVE_PT : page.marginPt,
          fontFamily: theme.fontFamily,
          fontSize: theme.baseFontSizePt,
          color: theme.textColor,
        }}
      >
        {headerBands.length > 0 && (
          <View
            style={{
              flexDirection: 'row',
              marginTop: layout.headerGapBeforePt ?? 0,
              marginBottom: headerGapAfter(layout, headerBands),
            }}
            // A masthead split across pages would print a logo with no address
            // beside it.
            wrap={false}
          >
            {Array.from({ length: headerColumns }, (_, i) => (
              <View
                key={i}
                style={{
                  flexGrow: columnWeight(columnWeights, i),
                  flexBasis: 0,
                  paddingRight: i < headerColumns - 1 ? COLUMN_GAP_PT : 0,
                }}
              >
                {headerBands
                  .filter((b) => (b.column ?? 1) === i + 1)
                  .map((b) => (
                    <View key={b.id} style={{ marginBottom: b.spaceAfterPt }}>
                      <BandContent ctx={ctx} band={b} />
                    </View>
                  ))}
              </View>
            ))}
          </View>
        )}

        {restBands.map((band) => (
          <View key={band.id} style={{ marginBottom: band.spaceAfterPt }}>
            <BandContent ctx={ctx} band={band} />
          </View>
        ))}

        {footer ? <FooterBandView ctx={ctx} band={footer} marginPt={page.marginPt} /> : null}
      </Page>
    </Document>
  );
}
